import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, useToast } from "../components/Navbar";

const Icon = ({ d, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);
const icons = {
  user:     "M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z",
  orders:   "M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2",
  support:  "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z",
  address:  "M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0zM12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6z",
  rewards:  "M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z",
  edit:     "M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z",
  chevron:  "M9 18l6-6-6-6",
  plus:     "M12 5v14M5 12h14",
  check:    "M20 6L9 17l-5-5",
  truck:    "M1 3h15v13H1zM16 8h4l3 3v5h-7V8zM5.5 21a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM18.5 21a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z",
  clock:    "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 6v6l4 2",
  x:        "M18 6L6 18M6 6l12 12",
  mail:     "M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2zM22 6l-10 7L2 6",
  phone:    "M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.07 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21 17z",
  home:     "M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM9 22V12h6v10",
  lock:     "M19 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2zM7 11V7a5 5 0 0 1 10 0v4",
  trash:    "M3 6h18M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2",
  arrow:    "M7 17L17 7M7 7h10v10",
};

const STATUS_CONFIG = {
  delivered:        { bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500" },
  shipped:          { bg: "bg-blue-50",    text: "text-blue-700",    dot: "bg-blue-500"    },
  processing:       { bg: "bg-amber-50",   text: "text-amber-700",   dot: "bg-amber-500"   },
  placed:           { bg: "bg-stone-100",  text: "text-stone-600",   dot: "bg-stone-400"   },
  out_for_delivery: { bg: "bg-orange-50",  text: "text-orange-700",  dot: "bg-orange-500"  },
  cancelled:        { bg: "bg-red-50",     text: "text-red-600",     dot: "bg-red-400"     },
  returned:         { bg: "bg-rose-50",    text: "text-rose-600",    dot: "bg-rose-400"    },
};

const SectionCard = ({ children, className = "", onClick }) => (
  <div onClick={onClick} className={`bg-white rounded-2xl border border-stone-100 shadow-sm ${className}`}>
    {children}
  </div>
);

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.placed;
  const label = status?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold tracking-wide ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {label}
    </span>
  );
};

const Field = ({ label, value, onChange, type = "text", icon, disabled = false, hint }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs font-semibold text-stone-400 uppercase tracking-widest">{label}</label>
    <div className="relative">
      {icon && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-300">
          <Icon d={icon} size={15} />
        </span>
      )}
      <input
        type={type} value={value} onChange={onChange} disabled={disabled}
        className={`w-full ${icon ? "pl-9" : "pl-3.5"} pr-3.5 py-2.5 rounded-xl border text-sm font-medium transition
          ${disabled
            ? "border-stone-100 bg-stone-50 text-stone-400 cursor-not-allowed"
            : "border-stone-200 bg-stone-50 text-stone-800 focus:outline-none focus:ring-2 focus:ring-stone-800 focus:border-transparent"
          }`}
      />
    </div>
    {hint && <p className="text-[11px] text-stone-400 leading-relaxed">{hint}</p>}
  </div>
);

const Spinner = () => (
  <div className="flex items-center justify-center py-16">
    <div className="w-6 h-6 border-2 border-stone-200 border-t-stone-600 rounded-full animate-spin" />
  </div>
);

const TABS = [
  { id: "profile",   label: "Profile",   icon: icons.user    },
  { id: "orders",    label: "Orders",    icon: icons.orders  },
  { id: "rewards",   label: "Rewards",   icon: icons.rewards },
  { id: "addresses", label: "Addresses", icon: icons.address },
  { id: "support",   label: "Support",   icon: icons.support },
];

// ─────────────────────────────────────────────────────────────────────────────
// PROFILE TAB
// ─────────────────────────────────────────────────────────────────────────────
const ProfileTab = () => {
  const { user, token, login } = useAuth();
  const { addToast } = useToast();
  const [name,      setName]      = useState(user?.name  || "");
  const [phone,     setPhone]     = useState(user?.phone || "");
  const [curPwd,    setCurPwd]    = useState("");
  const [newPwd,    setNewPwd]    = useState("");
  const [saving,    setSaving]    = useState(false);
  const [pwdSaving, setPwdSaving] = useState(false);
  const API = process.env.REACT_APP_API_URL || "http://localhost:5001";

  useEffect(() => { setName(user?.name || ""); setPhone(user?.phone || ""); }, [user]);

  const initials    = (user?.name || "?").split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
  const memberSince = user?.created_at
    ? new Date(user.created_at).toLocaleDateString("en-IN", { month: "long", year: "numeric" }) : null;

  async function handleSaveProfile() {
    if (!name.trim()) return addToast("Name cannot be empty", "error");
    setSaving(true);
    try {
      const res  = await fetch(`${API}/api/auth/profile`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: name.trim(), phone: phone.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      login(token, { ...user, name: data.name || name.trim(), phone: data.phone || phone.trim() });
      addToast("Profile updated!");
    } catch (err) { addToast(err.message || "Failed to save", "error"); }
    finally { setSaving(false); }
  }

  async function handleChangePassword() {
    if (!curPwd || !newPwd)  return addToast("Fill in both password fields", "error");
    if (newPwd.length < 6)   return addToast("New password must be at least 6 characters", "error");
    if (curPwd === newPwd)   return addToast("New password must differ from current", "error");
    setPwdSaving(true);
    try {
      const res  = await fetch(`${API}/api/auth/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ currentPassword: curPwd, newPassword: newPwd }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      addToast("Password changed successfully!");
      setCurPwd(""); setNewPwd("");
    } catch (err) { addToast(err.message || "Failed to change password", "error"); }
    finally { setPwdSaving(false); }
  }

  return (
    <div className="space-y-5">
      <SectionCard className="p-6">
        <div className="flex items-center gap-5">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-stone-700 to-stone-900 flex items-center justify-center text-white text-2xl font-bold tracking-tight select-none flex-shrink-0">
            {initials}
          </div>
          <div className="min-w-0">
            <p className="font-bold text-stone-900 text-lg leading-tight truncate">{user?.name || "—"}</p>
            <p className="text-sm text-stone-400 mt-0.5 truncate">{user?.email}</p>
            {memberSince && <p className="text-xs text-stone-300 mt-1">Member since {memberSince}</p>}
          </div>
        </div>
      </SectionCard>
      <SectionCard className="p-6">
        <h3 className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-5">Personal Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Field label="Full Name" value={name} onChange={e => setName(e.target.value)} />
          </div>
          <Field label="Phone" value={phone} onChange={e => setPhone(e.target.value)} type="tel" icon={icons.phone} />
          <Field label="Email" value={user?.email || ""} onChange={() => {}} type="email" icon={icons.mail} disabled hint="Email is your login identity and cannot be changed." />
        </div>
        <div className="flex justify-end mt-5">
          <button onClick={handleSaveProfile} disabled={saving}
            className="px-7 py-2.5 rounded-xl text-sm font-bold bg-stone-900 text-white hover:bg-stone-700 active:scale-95 transition-all duration-200 disabled:opacity-60">
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </SectionCard>
      <SectionCard className="p-6">
        <h3 className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-5">Change Password</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Current Password" value={curPwd} onChange={e => setCurPwd(e.target.value)} type="password" icon={icons.lock} />
          <Field label="New Password"     value={newPwd} onChange={e => setNewPwd(e.target.value)} type="password" icon={icons.lock} hint="Minimum 6 characters." />
        </div>
        <div className="flex justify-end mt-5">
          <button onClick={handleChangePassword} disabled={pwdSaving}
            className="px-7 py-2.5 rounded-xl text-sm font-bold bg-stone-900 text-white hover:bg-stone-700 active:scale-95 transition-all duration-200 disabled:opacity-60">
            {pwdSaving ? "Updating…" : "Update Password"}
          </button>
        </div>
      </SectionCard>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ORDERS TAB
// ─────────────────────────────────────────────────────────────────────────────
const OrdersTab = () => {
  const { token }  = useAuth();
  const navigate   = useNavigate();
  const [orders,   setOrders]  = useState([]);
  const [loading,  setLoading] = useState(true);
  const [error,    setError]   = useState(null);

  const API = process.env.REACT_APP_API_URL || "http://localhost:5001";
  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/orders`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => setOrders(Array.isArray(data) ? data : data.orders || []))
      .catch(() => setError("Could not load orders."))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <Spinner />;
  if (error)   return <p className="text-sm text-stone-400 text-center py-10">{error}</p>;
  if (!orders.length) return (
    <div className="text-center py-16 space-y-3">
      <p className="text-stone-400 text-sm">No orders yet.</p>
      <button onClick={() => navigate("/shop")} className="text-xs font-semibold text-rose-500 hover:underline">Start shopping →</button>
    </div>
  );

  return (
    <div className="space-y-3">
      <p className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-1">
        {orders.length} Order{orders.length !== 1 ? "s" : ""}
      </p>
      {orders.map((order) => {
        const itemCount = order.item_count ?? order.items?.length ?? 0;
        const dateStr   = order.placed_at
          ? new Date(order.placed_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
          : order.date || "";
        return (
          <SectionCard key={order.id} onClick={() => navigate(`/orders/${order.id}`)}
            className="p-4 hover:border-stone-200 transition cursor-pointer group">
            <div className="flex items-center justify-between gap-3">
              <div className="flex flex-col gap-0.5 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-stone-900 text-sm font-mono">{order.id}</span>
                  <StatusBadge status={order.status} />
                </div>
                <p className="text-xs text-stone-400 mt-0.5">
                  {dateStr}{itemCount ? ` · ${itemCount} item${itemCount !== 1 ? "s" : ""}` : ""}
                </p>
                {order.reward_points_earned > 0 && (
                  <p className="text-xs text-amber-600 font-semibold mt-0.5">🪙 +{order.reward_points_earned} pts earned</p>
                )}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="font-bold text-stone-900 text-sm">${Number(order.total || 0).toLocaleString()}</span>
                <span className="text-stone-300 group-hover:text-stone-500 transition">
                  <Icon d={icons.chevron} size={16} />
                </span>
              </div>
            </div>
          </SectionCard>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// REWARDS TAB
// ─────────────────────────────────────────────────────────────────────────────
const RewardsTab = () => {
  const { token } = useAuth();
  const navigate  = useNavigate();
  const [data,    setData]    = useState({ balance: 0, transactions: [] });
  const [loading, setLoading] = useState(true);

  const API = process.env.REACT_APP_API_URL || "http://localhost:5001";

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/rewards`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <Spinner />;

  const { balance, transactions } = data;
  const dollarValue = (balance / 100).toFixed(2);

  return (
    <div className="space-y-5">
      {/* Balance card */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 p-6 text-white shadow-lg">
        <div className="absolute -top-6 -right-6 w-32 h-32 bg-white/10 rounded-full" />
        <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-white/10 rounded-full" />
        <div className="relative">
          <p className="text-sm font-semibold text-amber-100 uppercase tracking-widest mb-1">Your Reward Points</p>
          <p className="text-5xl font-black tracking-tight">{balance.toLocaleString()}</p>
          <p className="text-amber-100 text-sm mt-1">≈ ${dollarValue} discount value</p>
          <div className="mt-4 bg-white/20 rounded-xl px-4 py-2.5 inline-flex items-center gap-2">
            <span className="text-lg">🪙</span>
            <span className="text-sm font-semibold">100 points = $1 off at checkout</span>
          </div>
        </div>
      </div>

      {/* How it works */}
      <SectionCard className="p-5">
        <h3 className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-4">How It Works</h3>
        <div className="space-y-3">
          {[
            { icon: "🛍️", title: "Shop & Earn",   desc: "Earn 1 point for every $1 you spend" },
            { icon: "🪙", title: "Accumulate",    desc: "Points add up with every order you place" },
            { icon: "💰", title: "Redeem at Checkout", desc: "Use points for discounts — 100 pts = $1 off (up to 20% of order)" },
          ].map(item => (
            <div key={item.title} className="flex items-start gap-3">
              <span className="text-2xl">{item.icon}</span>
              <div>
                <p className="text-sm font-bold text-stone-800">{item.title}</p>
                <p className="text-xs text-stone-400 mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Use points CTA */}
      {balance >= 100 && (
        <button onClick={() => navigate("/checkout")}
          className="w-full py-3.5 rounded-2xl bg-amber-500 hover:bg-amber-400 text-white font-bold text-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-amber-200">
          🪙 Use {balance} points at Checkout →
        </button>
      )}
      {balance > 0 && balance < 100 && (
        <div className="text-center text-xs text-stone-400 bg-stone-50 rounded-xl px-4 py-3">
          You need <span className="font-bold text-stone-600">{100 - balance} more points</span> to get your first $1 discount
        </div>
      )}

      {/* Transaction history */}
      <SectionCard>
        <div className="px-5 pt-5 pb-1">
          <h3 className="text-xs font-bold uppercase tracking-widest text-stone-400">Transaction History</h3>
        </div>
        {transactions.length === 0 ? (
          <p className="text-sm text-stone-400 text-center py-8">No transactions yet. Start shopping to earn points!</p>
        ) : (
          <div className="divide-y divide-stone-50 px-5">
            {transactions.map((tx) => (
              <div key={tx.id} className="flex items-center justify-between gap-3 py-3.5">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-lg ${tx.type === "earned" ? "bg-amber-50" : "bg-rose-50"}`}>
                    {tx.type === "earned" ? "🪙" : "💸"}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-stone-800 capitalize">
                      {tx.type === "earned" ? "Points Earned" : "Points Redeemed"}
                    </p>
                    <p className="text-xs text-stone-400 mt-0.5">{tx.note || (tx.order_ref ? `Order #${tx.order_ref}` : "")}</p>
                    <p className="text-[11px] text-stone-300 mt-0.5">
                      {new Date(tx.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                    </p>
                  </div>
                </div>
                <span className={`text-sm font-black ${tx.type === "earned" ? "text-amber-600" : "text-rose-500"}`}>
                  {tx.type === "earned" ? "+" : "-"}{tx.points} pts
                </span>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ADDRESSES TAB
// ─────────────────────────────────────────────────────────────────────────────
const EMPTY_FORM = {
  full_name: "", phone: "", address_line1: "", address_line2: "",
  city: "", state: "", zip: "", country: "United States", is_default: false,
};

const AddressesTab = () => {
  const { token }    = useAuth();
  const { addToast } = useToast();
  const [addresses, setAddresses] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showForm,  setShowForm]  = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [editId,    setEditId]    = useState(null);
  const [form,      setForm]      = useState(EMPTY_FORM);

  const API = process.env.REACT_APP_API || "http://localhost:5001";
  const loadAddresses = useCallback(() => {
    if (!token) return;
    setLoading(true);
    fetch(`${API}/api/addresses`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => setAddresses(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { loadAddresses(); }, [loadAddresses]);

  const f = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));

  const setDefault = async (id) => {
    try {
      const res = await fetch(`${API}/api/addresses/${id}/default`, {
        method: "PATCH", headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      setAddresses(prev => prev.map(a => ({ ...a, is_default: a.id === id ? 1 : 0 })));
    } catch { addToast("Failed to update default address", "error"); }
  };

  const deleteAddress = async (id) => {
    if (!window.confirm("Delete this address?")) return;
    try {
      const res = await fetch(`${API}/api/addresses/${id}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      setAddresses(prev => prev.filter(a => a.id !== id));
      addToast("Address deleted");
    } catch { addToast("Failed to delete address", "error"); }
  };

  const openNewForm  = () => { setEditId(null); setForm(EMPTY_FORM); setShowForm(true); };
  const openEditForm = (addr) => {
    setEditId(addr.id);
    setForm({ full_name: addr.full_name || "", phone: addr.phone || "", address_line1: addr.address_line1 || "", address_line2: addr.address_line2 || "", city: addr.city || "", state: addr.state || "", zip: addr.zip || "", country: addr.country || "United States", is_default: !!addr.is_default });
    setShowForm(true);
  };

  const handleSave = async () => {
    const { full_name, phone, address_line1, city, state, zip } = form;
    if (!full_name || !phone || !address_line1 || !city || !state || !zip)
      return addToast("Please fill all required fields", "error");
    setSaving(true);
    try {
      const url    = editId ? `${API}/api/addresses/${editId}` : `${API}/api/addresses`;
      const method = editId ? "PUT" : "POST";
      const res    = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      addToast(editId ? "Address updated!" : "Address saved!");
      setShowForm(false); setEditId(null); setForm(EMPTY_FORM);
      loadAddresses();
    } catch (err) { addToast(err.message || "Failed to save address", "error"); }
    finally { setSaving(false); }
  };

  const closeForm = () => { setShowForm(false); setEditId(null); setForm(EMPTY_FORM); };
  const inp = "w-full px-3.5 py-2.5 rounded-xl border border-stone-200 bg-stone-50 text-sm text-stone-800 font-medium focus:outline-none focus:ring-2 focus:ring-stone-800 focus:border-transparent transition";
  const lbl = "text-xs font-semibold text-stone-400 uppercase tracking-widest";

  if (loading) return <Spinner />;

  return (
    <div className="space-y-4">
      {addresses.length === 0 && !showForm && (
        <p className="text-center text-stone-400 text-sm py-8">No saved addresses yet.</p>
      )}
      {addresses.map((addr) => (
        <SectionCard key={addr.id} className={`p-5 ${addr.is_default ? "ring-2 ring-stone-900" : ""}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex gap-3.5 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-stone-100 flex items-center justify-center text-stone-500 shrink-0 mt-0.5">
                <Icon d={icons.home} size={16} />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-stone-900 text-sm">{addr.full_name}</span>
                  {addr.is_default === 1 && (
                    <span className="text-[10px] font-bold uppercase tracking-widest bg-stone-900 text-white px-2 py-0.5 rounded-full">Default</span>
                  )}
                </div>
                <p className="text-xs text-stone-400 mt-0.5">{addr.phone}</p>
                <p className="text-sm text-stone-500 mt-1">{addr.address_line1}{addr.address_line2 ? `, ${addr.address_line2}` : ""}</p>
                <p className="text-sm text-stone-500">{addr.city}, {addr.state} – {addr.zip}</p>
                <p className="text-xs text-stone-400">{addr.country}</p>
              </div>
            </div>
            <div className="flex flex-col gap-2 items-end shrink-0">
              <button onClick={() => openEditForm(addr)} className="text-xs font-semibold text-stone-500 hover:text-stone-900 transition flex items-center gap-1">
                <Icon d={icons.edit} size={12} /> Edit
              </button>
              {!addr.is_default && (
                <button onClick={() => setDefault(addr.id)} className="text-xs font-semibold text-stone-400 hover:text-stone-700 transition">Set default</button>
              )}
              <button onClick={() => deleteAddress(addr.id)} className="text-xs font-semibold text-red-400 hover:text-red-600 transition flex items-center gap-1">
                <Icon d={icons.trash} size={11} /> Delete
              </button>
            </div>
          </div>
        </SectionCard>
      ))}
      {!showForm && (
        <button onClick={openNewForm} className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl border-2 border-dashed border-stone-200 text-stone-400 hover:border-stone-400 hover:text-stone-600 transition text-sm font-semibold">
          <Icon d={icons.plus} size={16} /> Add New Address
        </button>
      )}
      {showForm && (
        <SectionCard className="p-5">
          <h3 className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-4">{editId ? "Edit Address" : "New Address"}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5"><label className={lbl}>Full Name *</label><input className={inp} value={form.full_name} onChange={f("full_name")} placeholder="Full name" /></div>
            <div className="flex flex-col gap-1.5"><label className={lbl}>Phone *</label><input className={inp} value={form.phone} onChange={f("phone")} placeholder="+1 555 000 0000" type="tel" /></div>
            <div className="sm:col-span-2 flex flex-col gap-1.5"><label className={lbl}>Address Line 1 *</label><input className={inp} value={form.address_line1} onChange={f("address_line1")} placeholder="Street address" /></div>
            <div className="sm:col-span-2 flex flex-col gap-1.5"><label className={lbl}>Address Line 2 <span className="normal-case font-normal text-stone-300">(optional)</span></label><input className={inp} value={form.address_line2} onChange={f("address_line2")} placeholder="Apt, suite, unit" /></div>
            <div className="flex flex-col gap-1.5"><label className={lbl}>City *</label><input className={inp} value={form.city} onChange={f("city")} placeholder="New York" /></div>
            <div className="flex flex-col gap-1.5"><label className={lbl}>State *</label><input className={inp} value={form.state} onChange={f("state")} placeholder="New York" /></div>
            <div className="flex flex-col gap-1.5"><label className={lbl}>ZIP Code *</label><input className={inp} value={form.zip} onChange={f("zip")} placeholder="10001" /></div>
            <div className="flex flex-col gap-1.5"><label className={lbl}>Country</label><input className={inp} value={form.country} onChange={f("country")} placeholder="United States" /></div>
            <div className="sm:col-span-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_default} onChange={e => setForm(p => ({ ...p, is_default: e.target.checked }))} className="w-4 h-4 rounded" />
                <span className="text-sm text-stone-600">Set as default address</span>
              </label>
            </div>
          </div>
          <div className="flex gap-2 mt-4 justify-end">
            <button onClick={closeForm} className="px-5 py-2 rounded-xl text-sm font-semibold text-stone-500 hover:bg-stone-100 transition">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="px-5 py-2 rounded-xl text-sm font-bold bg-stone-900 text-white hover:bg-stone-700 transition disabled:opacity-60">
              {saving ? "Saving…" : editId ? "Update Address" : "Save Address"}
            </button>
          </div>
        </SectionCard>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SUPPORT TAB
// ─────────────────────────────────────────────────────────────────────────────
const FAQS = [
  { q: "How do I track my order?",          a: "Go to the Orders tab and tap any order to see live tracking updates." },
  { q: "Can I change my delivery address?", a: "Yes, if the order hasn't shipped yet. Contact us within 2 hours of placing." },
  { q: "What's the return policy?",         a: "We accept returns within 7 days of delivery for most items in original condition." },
  { q: "How long does delivery take?",      a: "Standard delivery is 3–5 business days. Express is 1–2 days." },
  { q: "How do reward points work?",        a: "You earn 1 point per $1 spent. 100 points = $1 discount at checkout. Points are applied automatically when you toggle them on in the Order Summary step." },
];

const SupportTab = () => {
  const { token }    = useAuth();
  const { addToast } = useToast();
  const [open,    setOpen]    = useState(null);
  const [subject, setSubject] = useState("");
  const [msg,     setMsg]     = useState("");
  const [sending, setSending] = useState(false);
  const API = process.env.REACT_APP_API_URL || "http://localhost:5001";

  const handleSend = async () => {
    if (!msg.trim()) return addToast("Please describe your issue", "error");
    setSending(true);
    try {
      await fetch(`${API}/api/support/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ subject, message: msg }),
      });
    } finally {
      addToast("Message sent! We'll reply within 24 hours.");
      setMsg(""); setSubject(""); setSending(false);
    }
  };

  const inp = "w-full px-3.5 py-2.5 rounded-xl border border-stone-200 bg-stone-50 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:ring-2 focus:ring-stone-800 focus:border-transparent transition";

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3">
        {[
          { label: "Email Us", sub: "Reply within 24h", icon: icons.mail,  color: "bg-blue-50 text-blue-700" },
          { label: "Call Us",  sub: "Mon–Sat, 9am–6pm", icon: icons.phone, color: "bg-emerald-50 text-emerald-700" },
        ].map((opt) => (
          <SectionCard key={opt.label} className="p-4 cursor-pointer hover:border-stone-200 transition">
            <div className={`w-9 h-9 rounded-xl ${opt.color} flex items-center justify-center mb-3`}>
              <Icon d={opt.icon} size={16} />
            </div>
            <p className="font-bold text-stone-900 text-sm">{opt.label}</p>
            <p className="text-xs text-stone-400 mt-0.5">{opt.sub}</p>
          </SectionCard>
        ))}
      </div>
      <SectionCard>
        <div className="p-5 pb-0"><h3 className="text-xs font-bold uppercase tracking-widest text-stone-400">FAQs</h3></div>
        <div className="divide-y divide-stone-50 mt-3">
          {FAQS.map((faq, i) => (
            <div key={i} className="px-5 py-3.5 cursor-pointer" onClick={() => setOpen(open === i ? null : i)}>
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-stone-800">{faq.q}</p>
                <span className={`text-stone-400 transition-transform duration-200 shrink-0 ${open === i ? "rotate-90" : ""}`}>
                  <Icon d={icons.chevron} size={15} />
                </span>
              </div>
              {open === i && <p className="text-sm text-stone-500 mt-2 leading-relaxed">{faq.a}</p>}
            </div>
          ))}
        </div>
      </SectionCard>
      <SectionCard className="p-5">
        <h3 className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-4">Send a Message</h3>
        <div className="space-y-3">
          <input className={inp} placeholder="Subject (optional)" value={subject} onChange={e => setSubject(e.target.value)} />
          <textarea value={msg} onChange={e => setMsg(e.target.value)} rows={4} placeholder="Describe your issue…" className={inp + " resize-none"} />
        </div>
        <div className="flex justify-end mt-3">
          <button onClick={handleSend} disabled={sending} className="px-6 py-2.5 rounded-xl text-sm font-bold bg-stone-900 text-white hover:bg-stone-700 active:scale-95 transition-all duration-200 disabled:opacity-60">
            {sending ? "Sending…" : "Send Message"}
          </button>
        </div>
      </SectionCard>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN SETTINGS PAGE
// ─────────────────────────────────────────────────────────────────────────────
export default function Settings() {
  const location             = useLocation();
  const navigate             = useNavigate();
  const { user, isLoggedIn } = useAuth();
  const [activeTab, setActiveTab] = useState(location.state?.tab || "profile");

  useEffect(() => { if (!isLoggedIn) navigate("/"); }, [isLoggedIn, navigate]);
  useEffect(() => { if (location.state?.tab) setActiveTab(location.state.tab); }, [location.state]);

  if (!isLoggedIn) return null;

  const TAB_CONTENT = {
    profile:   <ProfileTab />,
    orders:    <OrdersTab />,
    rewards:   <RewardsTab />,
    addresses: <AddressesTab />,
    support:   <SupportTab />,
  };

  return (
    <div className="min-h-screen bg-stone-50 font-sans">
      <div className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
          <h1 className="text-base font-bold text-stone-900 tracking-tight">Settings</h1>
          <p className="text-xs text-stone-400 truncate max-w-[200px]">{user?.email}</p>
        </div>
      </div>
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex gap-1 bg-white rounded-2xl p-1.5 border border-stone-100 shadow-sm mb-6">
          {TABS.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex flex-col items-center gap-1 py-2.5 px-1 rounded-xl text-xs font-bold transition-all duration-200 ${
                activeTab === tab.id
                  ? "bg-stone-900 text-white shadow-sm"
                  : "text-stone-400 hover:text-stone-700 hover:bg-stone-50"
              }`}>
              <Icon d={tab.icon} size={17} />
              <span className="hidden sm:block">{tab.label}</span>
            </button>
          ))}
        </div>
        <div key={activeTab}>{TAB_CONTENT[activeTab]}</div>
      </div>
    </div>
  );
}